import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import useLocalePath from '../i18n/useLocalePath';
import ResponsiveImage from '../components/ResponsiveImage';
import posts, { loadBody, formatPostDate } from '../content/blog';

const BASE = 'https://www.nekochan.app';
const DEFAULT_IMAGE = `${BASE}/social-cover.png`;

/* Post bodies link to other pages with absolute canonical URLs. Route those
   through the router so they stay in-app and in the reader's language tree;
   anything off-site opens in a new tab. */
function MarkdownLink({ href = '', children }) {
  const { lp } = useLocalePath();
  if (href.startsWith(BASE)) {
    const path = href.slice(BASE.length) || '/';
    const isLocalized = path === '/es' || path.startsWith('/es/');
    return <Link to={isLocalized ? path : lp(path)}>{children}</Link>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

const markdownComponents = { a: MarkdownLink };

export default function BlogPost() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { isSpanish, lp } = useLocalePath();
  const lang = isSpanish ? 'es' : 'en';
  const post = posts[lang].find(p => p.slug === slug);

  const key = `${lang}/${slug}`;
  const [loaded, setLoaded] = useState({ key: null });

  useEffect(() => {
    if (!post) return undefined;
    let cancelled = false;
    loadBody(lang, post.slug)
      .then(text => !cancelled && setLoaded({ key, text }))
      .catch(() => !cancelled && setLoaded({ key, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [key, lang, post]);

  if (!post) {
    /* The language toggle keeps the slug, so /es/blog/<english-slug> (and the
       reverse) lands here: forward it to the translated post. */
    const other = posts[isSpanish ? 'en' : 'es'].find(p => p.slug === slug);
    if (other?.pairSlug) {
      return <Navigate to={lp(`/blog/${other.pairSlug}`)} replace />;
    }

    return (
      <div className="bg-white">
        <Helmet>
          <title>{`${t('blogPost.notFound')} | Nekochan`}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-center font-rubik">
          <h1 className="font-bold text-h3 text-gray-900 mb-4">
            {t('blogPost.notFound')}
          </h1>
          <Link to={lp('/blog')} className="text-[#0a8f8f] hover:underline">
            ← {t('blogPost.back')}
          </Link>
        </div>
      </div>
    );
  }

  const enSlug = isSpanish ? post.pairSlug : post.slug;
  const esSlug = isSpanish ? post.slug : post.pairSlug;
  const enUrl = enSlug && `${BASE}/blog/${enSlug}`;
  const esUrl = esSlug && `${BASE}/es/blog/${esSlug}`;
  const hasPair = Boolean(enUrl && esUrl);
  const canonical = isSpanish ? esUrl : enUrl;
  // post.image is a base path (/images/blog/<slug>) with .avif/.webp/.jpg
  // variants; social cards and JSON-LD need one absolute URL, so use the JPG.
  const image = post.image ? `${BASE}${post.image}.jpg` : DEFAULT_IMAGE;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    inLanguage: lang,
    image,
    mainEntityOfPage: canonical,
    author: { '@type': 'Organization', name: 'Nekochan', url: BASE },
    publisher: { '@type': 'Organization', name: 'Nekochan', url: BASE },
  };

  const body = loaded.key === key ? loaded : null;

  return (
    <div className="bg-white">
      {/* defer={false} applies head tags synchronously: helmet-async's default
          requestAnimationFrame deferral never fires in hidden/headless tabs
          (some crawlers, background tabs), which would drop the canonical,
          hreflang and JSON-LD for the post. */}
      <Helmet defer={false}>
        <title>{`${post.title} | Nekochan`}</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={canonical} />
        {hasPair && <link rel="alternate" href={enUrl} hrefLang="en" />}
        {hasPair && <link rel="alternate" href={esUrl} hrefLang="es" />}
        {hasPair && <link rel="alternate" href={enUrl} hrefLang="x-default" />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:image" content={image} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Nekochan" />
        <meta property="og:locale" content={isSpanish ? 'es_LA' : 'en_US'} />
        <meta property="article:published_time" content={post.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <meta name="twitter:image" content={image} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto px-4 py-12 md:py-16 font-rubik"
      >
        <Link
          to={lp('/blog')}
          className="inline-block text-[#0a8f8f] hover:underline text-bodyS mb-8"
        >
          ← {t('blogPost.back')}
        </Link>

        <header className="mb-8">
          <h1 className="font-bold text-h2 md:text-h1 text-gray-900 mb-3">
            {post.title}
          </h1>
          <time className="text-bodyS text-gray-500" dateTime={post.date}>
            {formatPostDate(post.date, lang)}
          </time>
        </header>

        {post.image && (
          <ResponsiveImage
            srcBase={post.image}
            fallbackExt="jpg"
            alt=""
            decoding="async"
            className="mb-10 aspect-video w-full rounded-2xl object-cover bg-brand/5"
          />
        )}

        {!body && <div className="min-h-[60vh]" aria-busy="true" />}
        {body?.failed && (
          <p className="text-body text-gray-600">{t('blogPost.loadError')}</p>
        )}
        {body?.text && (
          <article className="prose prose-lg max-w-none prose-headings:font-rubik prose-headings:text-gray-900 prose-a:text-[#0a8f8f] hover:prose-a:text-[#0fa8a8]">
            <ReactMarkdown components={markdownComponents}>{body.text}</ReactMarkdown>
          </article>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <Link
            to={lp('/blog')}
            className="text-[#0a8f8f] hover:underline font-medium"
          >
            ← {t('blogPost.back')}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
